import { useEffect, useState } from 'react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { fetchAdminAnalytics } from '@/lib/api'

type AnalyticsData = {
  monthly: { month: string; leads: number; revenue: number }[]
  model_breakdown: { model: string; count: number }[]
  top_options: { name: string; count: number }[]
  status_breakdown: { status: string; count: number }[]
  conversion_stats: { total: number; converted: number; avg_value: number }
  funnel: { event_type: string; sessions: number }[]
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</div>
      <div className="text-3xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  )
}

function HBar({ label, value, max, color = '#f59e0b' }: { label: string; value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="flex items-center gap-3">
      <div className="w-36 text-xs text-slate-600 truncate shrink-0">{label}</div>
      <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="text-xs font-semibold text-slate-700 w-6 text-right">{value}</div>
    </div>
  )
}

function MonthChart({ data }: { data: { month: string; leads: number; revenue: number }[] }) {
  if (data.length === 0) return <div className="text-slate-400 text-sm text-center py-8">No data yet.</div>

  const maxLeads = Math.max(...data.map((d) => d.leads), 1)
  const maxRev = Math.max(...data.map((d) => d.revenue), 1)
  const svgH = 120
  const barW = Math.max(18, Math.floor((480 - data.length * 4) / data.length))

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${Math.max(480, data.length * (barW + 6))} ${svgH + 40}`} className="w-full min-w-[320px]">
        {data.map((d, i) => {
          const lH = (d.leads / maxLeads) * svgH
          const rH = (d.revenue / maxRev) * svgH
          const x = i * (barW + 6) + 3
          const label = d.month.slice(5) // MM
          return (
            <g key={d.month}>
              {/* Revenue bar (behind) */}
              <rect x={x} y={svgH - rH} width={barW} height={rH} fill="#FEF3C7" rx={3} />
              {/* Leads bar (front, narrower) */}
              <rect x={x + barW * 0.2} y={svgH - lH} width={barW * 0.5} height={lH} fill="#F59E0B" rx={2} />
              {/* Month label */}
              <text x={x + barW / 2} y={svgH + 16} textAnchor="middle" fontSize={9} fill="#94A3B8" fontFamily="system-ui">
                {label}
              </text>
              {/* Lead count */}
              {d.leads > 0 && (
                <text x={x + barW / 2} y={svgH - lH - 3} textAnchor="middle" fontSize={8} fill="#92400E" fontFamily="system-ui" fontWeight="600">
                  {d.leads}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      <div className="flex items-center gap-4 mt-1 justify-center">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <div className="w-3 h-3 rounded bg-amber-400" /> Leads
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <div className="w-3 h-3 rounded bg-amber-100" /> Revenue (relative)
        </div>
      </div>
    </div>
  )
}

const STATUS_LABELS: Record<string, string> = {
  new: 'New', reviewed: 'Reviewed', contacted: 'Contacted',
  estimate_sent: 'Estimate Sent', deposit_paid: 'Deposit Paid',
  construction: 'In Construction', won: 'Won', lost: 'Lost', archived: 'Archived',
}
const STATUS_COLORS: Record<string, string> = {
  new: '#60A5FA', reviewed: '#A78BFA', contacted: '#34D399',
  estimate_sent: '#FBBF24', deposit_paid: '#F59E0B', construction: '#10B981',
  won: '#059669', lost: '#F87171', archived: '#94A3B8',
}

export default function AdminAnalytics() {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAdminAnalytics()
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return (
    <AdminLayout>
      <PageHeader title="Analytics" subtitle="Builder behavior and conversion tracking" />
      <div className="text-slate-400 text-sm">Loading…</div>
    </AdminLayout>
  )

  if (error || !data) return (
    <AdminLayout>
      <PageHeader title="Analytics" subtitle="Builder behavior and conversion tracking" />
      <div className="text-red-600 text-sm">{error ?? 'Failed to load'}</div>
    </AdminLayout>
  )

  const convRate = data.conversion_stats.total > 0
    ? Math.round((data.conversion_stats.converted / data.conversion_stats.total) * 100)
    : 0

  const totalRevenue = data.monthly.reduce((s, d) => s + d.revenue, 0)
  const maxModel = Math.max(...data.model_breakdown.map((m) => m.count), 1)
  const maxOption = Math.max(...data.top_options.map((o) => o.count), 1)
  const maxStatus = Math.max(...data.status_breakdown.map((s) => s.count), 1)

  return (
    <AdminLayout>
      <PageHeader title="Analytics" subtitle="Lead volume, revenue trends, and popular configurations" />

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Leads" value={String(data.conversion_stats.total)} />
        <StatCard label="Conversion Rate" value={`${convRate}%`} sub="Deposit + won + construction" />
        <StatCard label="Avg Build Value" value={`$${Math.round(data.conversion_stats.avg_value).toLocaleString()}`} />
        <StatCard
          label="Pipeline Revenue"
          value={`$${Math.round(totalRevenue / 1000)}k`}
          sub="Sum of all leads (12 mo)"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly trend */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Monthly Leads — Last 12 Months</h3>
          <MonthChart data={data.monthly} />
        </div>

        {/* Status breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Lead Pipeline</h3>
          <div className="space-y-3">
            {data.status_breakdown.map((s) => (
              <HBar
                key={s.status}
                label={STATUS_LABELS[s.status] ?? s.status}
                value={s.count}
                max={maxStatus}
                color={STATUS_COLORS[s.status] ?? '#94A3B8'}
              />
            ))}
            {data.status_breakdown.length === 0 && (
              <div className="text-slate-400 text-sm">No leads yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Model breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Leads by Model</h3>
          <div className="space-y-3">
            {data.model_breakdown.map((m) => (
              <HBar key={m.model} label={m.model} value={m.count} max={maxModel} color="#8B5CF6" />
            ))}
            {data.model_breakdown.length === 0 && <div className="text-slate-400 text-sm">No data.</div>}
          </div>
        </div>

        {/* Top options */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Most Selected Upgrades</h3>
          <div className="space-y-3">
            {data.top_options.map((o) => (
              <HBar key={o.name} label={o.name} value={o.count} max={maxOption} color="#10B981" />
            ))}
            {data.top_options.length === 0 && <div className="text-slate-400 text-sm">No data.</div>}
          </div>
        </div>
      </div>

      {/* Builder funnel */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-1">Builder Funnel — Last 30 Days</h3>
        <p className="text-xs text-slate-400 mb-4">Unique sessions reaching each step</p>
        {data.funnel.length === 0 ? (
          <div className="text-slate-400 text-sm">No funnel data yet — tracking starts once visitors use the builder.</div>
        ) : (
          <FunnelChart funnel={data.funnel} />
        )}
      </div>
    </AdminLayout>
  )
}

const FUNNEL_STEPS = [
  { key: 'session_start',   label: 'Sessions Started' },
  { key: 'model_selected',  label: 'Model Selected' },
  { key: 'step_viewed',     label: 'Customized' },
  { key: 'review_viewed',   label: 'Reviewed Build' },
  { key: 'form_started',    label: 'Started Quote Form' },
  { key: 'lead_submitted',  label: 'Submitted Lead' },
]

function FunnelChart({ funnel }: { funnel: { event_type: string; sessions: number }[] }) {
  const map = Object.fromEntries(funnel.map((f) => [f.event_type, f.sessions]))
  const top = Math.max(...FUNNEL_STEPS.map((s) => map[s.key] ?? 0), 1)

  return (
    <div className="space-y-2">
      {FUNNEL_STEPS.map((step, i) => {
        const count = map[step.key] ?? 0
        const prev = i === 0 ? count : (map[FUNNEL_STEPS[i - 1].key] ?? 0)
        const dropPct = prev > 0 && i > 0 ? Math.round((1 - count / prev) * 100) : null
        const pct = top > 0 ? Math.round((count / top) * 100) : 0

        return (
          <div key={step.key} className="flex items-center gap-3">
            <div className="w-36 text-xs text-slate-600 shrink-0">{step.label}</div>
            <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
              <div
                className="h-full rounded-full flex items-center px-2 transition-all"
                style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: '#3B82F6' }}
              >
                {count > 0 && <span className="text-white text-[10px] font-semibold">{count}</span>}
              </div>
            </div>
            {dropPct !== null && dropPct > 0 && (
              <div className="text-[10px] text-red-400 w-14 text-right shrink-0">−{dropPct}% drop</div>
            )}
          </div>
        )
      })}
    </div>
  )
}
